import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Zap, DollarSign, Calendar, Users, AlertTriangle, CheckCircle, 
  Clock, Send, RefreshCw, Play, Pause, FileText, TrendingUp,
  Bell, MessageCircle, Mail, Search, Filter, Eye, ChevronRight,
  Settings, Target, BarChart3, ArrowUpRight, ArrowDownRight, Sparkles,
  Heart, AlertOctagon, Smile, Frown, Meh
} from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export default function DnaPayCobrancasAutomaticas() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [regua, setRegua] = useState(null);
  const [titulos, setTitulos] = useState([]);
  const [notificacoes, setNotificacoes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [selectedTitulo, setSelectedTitulo] = useState(null);
  
  // Estados para mensagens inteligentes
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mensagensPendentes, setMensagensPendentes] = useState([]);
  const [performanceMensagens, setPerformanceMensagens] = useState(null);
  const [previewMensagem, setPreviewMensagem] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Estados para geração em lote
  const [showGerarLote, setShowGerarLote] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [loteConfig, setLoteConfig] = useState({
    categoria: 'mensalidade',
    competencia: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    usar_plano_financeiro: true,
    valor_fixo: null,
    descricao_custom: ''
  });
  const [resultadoLote, setResultadoLote] = useState(null);
  
  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [filtroCategoria, setFiltroCategoria] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categorias = [
    { value: 'mensalidade', label: 'Mensalidade' },
    { value: 'matricula', label: 'Matrícula' },
    { value: 'material', label: 'Material Didático' },
    { value: 'uniforme', label: 'Uniforme' },
    { value: 'evento', label: 'Evento/Passeio' },
    { value: 'aula_extra', label: 'Aula Extra' },
    { value: 'taxa', label: 'Taxa' },
    { value: 'outros', label: 'Outros' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, reguaRes, titulosRes, notifRes, templatesRes, msgPendRes, perfRes] = await Promise.all([
        api.get('/dnapay/charge-automation/dashboard'),
        api.get('/dnapay/charge-automation/regua/padrao'),
        api.get('/dnapay/billing/titulos?limit=100'),
        api.get('/dnapay/charge-automation/notificacoes/pendentes?limit=50'),
        api.get('/dnapay/smart-messages/templates'),
        api.get('/dnapay/smart-messages/pendentes?limit=50'),
        api.get('/dnapay/smart-messages/performance')
      ]);
      
      setDashboard(dashRes.data);
      setRegua(reguaRes.data);
      setTitulos(titulosRes.data || []);
      setNotificacoes(notifRes.data?.notificacoes || []);
      setTemplates(templatesRes.data?.templates || []);
      setMensagensPendentes(msgPendRes.data?.mensagens || []);
      setPerformanceMensagens(perfRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarLote = async () => {
    if (!loteConfig.competencia) {
      toast.error('Informe a competência');
      return;
    }

    setGerando(true);
    try {
      const response = await api.post('/dnapay/charge-automation/gerar-lote', loteConfig);
      setResultadoLote(response.data);
      toast.success(`${response.data.total_sucesso} títulos gerados com sucesso!`);
      loadData();
    } catch (error) {
      toast.error('Erro ao gerar títulos');
    } finally {
      setGerando(false);
    }
  };

  const handleExecutarRegua = async () => {
    try {
      const response = await api.post('/dnapay/charge-automation/executar-regua');
      toast.success(`Régua executada: ${response.data.notificacoes_enviadas} notificações geradas`);
      loadData();
    } catch (error) {
      toast.error('Erro ao executar régua');
    }
  };

  const handleExecutarReguaMensagens = async () => {
    try {
      const response = await api.post('/dnapay/smart-messages/executar-regua');
      toast.success(`Mensagens geradas: ${response.data.mensagens_geradas} mensagens`);
      loadData();
    } catch (error) {
      toast.error('Erro ao executar régua de mensagens');
    }
  };

  const handlePreviewMensagem = async (tituloId, diasRelativo) => {
    try {
      const response = await api.get(`/dnapay/smart-messages/preview/${tituloId}/${diasRelativo}`);
      setPreviewMensagem(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Erro ao gerar preview');
    }
  };

  const handleEnviarMensagem = async (tituloId, diasRelativo) => {
    try {
      await api.post(`/dnapay/smart-messages/enviar/${tituloId}?dias_relativo=${diasRelativo}`);
      toast.success('Mensagem enviada com sucesso!');
      loadData();
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    }
  };

  const handlePausarCobranca = async (tituloId) => {
    try {
      await api.post(`/dnapay/charge-automation/pausar-cobranca/${tituloId}`);
      toast.success('Cobrança pausada');
      loadData();
    } catch (error) {
      toast.error('Erro ao pausar cobrança');
    }
  };

  const handleRetomarCobranca = async (tituloId) => {
    try {
      await api.post(`/dnapay/charge-automation/retomar-cobranca/${tituloId}`);
      toast.success('Cobrança retomada');
      loadData();
    } catch (error) {
      toast.error('Erro ao retomar cobrança');
    }
  };

  const loadTimeline = async (tituloId) => {
    try {
      const response = await api.get(`/dnapay/charge-automation/timeline/${tituloId}`);
      setTimeline(response.data.eventos || []);
    } catch (error) {
      toast.error('Erro ao carregar timeline');
    }
  };

  const filteredTitulos = titulos.filter(t => {
    if (filtroStatus !== 'all' && t.status !== filtroStatus) return false;
    if (filtroCategoria !== 'all' && t.categoria !== filtroCategoria) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        t.aluno_nome?.toLowerCase().includes(term) ||
        t.responsavel_nome?.toLowerCase().includes(term) ||
        t.descricao?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pendente: 'dna-tone-warning',
      pago: 'dna-tone-success',
      vencido: 'dna-tone-danger',
      cancelado: 'dna-tone-neutral'
    };
    return <Badge className={styles[status] || 'dna-tone-neutral'}>{status}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="cobrancas-automaticas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-7 h-7 text-purple-600" />
            Cobranças Automáticas
          </h1>
          <p className="text-muted-foreground mt-1">
            Régua de cobrança inteligente e geração automatizada
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setShowGerarLote(true)} className="bg-purple-600 hover:bg-purple-700">
            <Play className="w-4 h-4 mr-2" />
            Gerar em Lote
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="titulos" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Títulos
          </TabsTrigger>
          <TabsTrigger value="mensagens" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Mensagens
          </TabsTrigger>
          <TabsTrigger value="regua" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Régua
          </TabsTrigger>
          <TabsTrigger value="notificacoes" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notificações
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboard && (
            <>
              {/* Cards de Resumo */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-muted-foreground">Total de Títulos</p>
                        <p className="text-3xl font-bold">{dashboard.resumo.total_titulos}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-blue-100">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-muted-foreground">Em Aberto</p>
                        <p className="text-3xl font-bold">{formatCurrency(dashboard.valores.total_em_aberto)}</p>
                        <p className="text-xs text-muted-foreground">{dashboard.resumo.titulos_pendentes} títulos</p>
                      </div>
                      <div className="p-2 rounded-lg bg-yellow-100">
                        <Clock className="w-5 h-5 text-yellow-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-muted-foreground">Vencidos</p>
                        <p className="text-3xl font-bold">{formatCurrency(dashboard.valores.total_vencido)}</p>
                        <p className="text-xs text-muted-foreground">{dashboard.resumo.titulos_vencidos} títulos</p>
                      </div>
                      <div className="p-2 rounded-lg bg-red-100">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-muted-foreground">Recebido (Mês)</p>
                        <p className="text-3xl font-bold">{formatCurrency(dashboard.valores.recebido_mes_atual)}</p>
                        <p className="text-xs text-muted-foreground">{dashboard.resumo.titulos_pagos} pagos</p>
                      </div>
                      <div className="p-2 rounded-lg bg-green-100">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Indicadores */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-orange-100">
                        <Users className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Famílias Inadimplentes</p>
                        <p className="text-2xl font-bold">{dashboard.indicadores.familias_inadimplentes}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-purple-100">
                        <Bell className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Notificações Hoje</p>
                        <p className="text-2xl font-bold">{dashboard.indicadores.notificacoes_enviadas_hoje}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full ${dashboard.indicadores.taxa_inadimplencia > 10 ? 'bg-red-100' : 'bg-green-100'}`}>
                        <BarChart3 className={`w-6 h-6 ${dashboard.indicadores.taxa_inadimplencia > 10 ? 'text-red-600' : 'text-green-600'}`} />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Taxa Inadimplência</p>
                        <p className="text-2xl font-bold">{dashboard.indicadores.taxa_inadimplencia}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Ações Rápidas */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-600" />
                    Ações Rápidas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Button 
                      variant="outline" 
                      className="h-20 flex flex-col items-center justify-center gap-2"
                      onClick={handleExecutarRegua}
                    >
                      <Send className="w-6 h-6 text-blue-600" />
                      <span>Executar Régua Agora</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-20 flex flex-col items-center justify-center gap-2"
                      onClick={() => setShowGerarLote(true)}
                    >
                      <Play className="w-6 h-6 text-green-600" />
                      <span>Gerar Cobranças em Lote</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-20 flex flex-col items-center justify-center gap-2"
                      onClick={() => setActiveTab('notificacoes')}
                    >
                      <Bell className="w-6 h-6 text-orange-600" />
                      <span>Ver Notificações Pendentes</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Títulos Tab */}
        <TabsContent value="titulos" className="space-y-4">
          {/* Filtros */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Buscar aluno, responsável..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="vencido">Vencido</SelectItem>
                    <SelectItem value="pago">Pago</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                  <SelectTrigger>
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas categorias</SelectItem>
                    {categorias.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-right text-sm text-muted-foreground self-center">
                  {filteredTitulos.length} títulos
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabela */}
          <Card>
            <CardContent className="pt-4">
              <div className="overflow-x-auto dna-responsive-table sticky-first-col">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aluno</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Régua</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTitulos.slice(0, 50).map((titulo) => (
                      <TableRow key={titulo.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{titulo.aluno_nome}</p>
                            <p className="text-xs text-muted-foreground">{titulo.turma_nome}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p>{titulo.descricao}</p>
                            <p className="text-xs text-muted-foreground">{titulo.competencia}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {titulo.vencimento?.substring(0, 10)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{formatCurrency(titulo.saldo_atualizado || titulo.valor_final)}</p>
                            {titulo.multa > 0 && (
                              <p className="text-xs text-red-600">+{formatCurrency(titulo.multa + titulo.juros)} encargos</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(titulo.status)}</TableCell>
                        <TableCell>
                          {titulo.regua_ativa !== false ? (
                            <Badge className="bg-green-100 text-green-800">Ativa</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Pausada</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedTitulo(titulo);
                                loadTimeline(titulo.id);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {titulo.status !== 'pago' && titulo.status !== 'cancelado' && (
                              titulo.regua_ativa !== false ? (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handlePausarCobranca(titulo.id)}
                                >
                                  <Pause className="w-4 h-4 text-orange-600" />
                                </Button>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleRetomarCobranca(titulo.id)}
                                >
                                  <Play className="w-4 h-4 text-green-600" />
                                </Button>
                              )
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mensagens Inteligentes Tab */}
        <TabsContent value="mensagens" className="space-y-4">
          {/* Header com Performance */}
          {performanceMensagens && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Mensagens</p>
                      <p className="text-2xl font-bold">{performanceMensagens.resumo.total_mensagens}</p>
                    </div>
                    <MessageCircle className="w-8 h-8 text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">Taxa de Entrega</p>
                      <p className="text-2xl font-bold">{performanceMensagens.resumo.taxa_entrega}%</p>
                    </div>
                    <Send className="w-8 h-8 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">Taxa de Leitura</p>
                      <p className="text-2xl font-bold">{performanceMensagens.resumo.taxa_leitura}%</p>
                    </div>
                    <Eye className="w-8 h-8 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">Pendentes</p>
                      <p className="text-2xl font-bold">{mensagensPendentes.length}</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Ações */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Mensagens Inteligentes
              </CardTitle>
              <CardDescription>
                Sistema de comunicação humanizada e personalizada
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <Button onClick={handleExecutarReguaMensagens} className="bg-purple-600 hover:bg-purple-700">
                  <Send className="w-4 h-4 mr-2" />
                  Executar Régua de Mensagens
                </Button>
              </div>

              {/* Templates de Mensagem */}
              <div className="space-y-3">
                <h3 className="font-medium text-lg mb-4">Templates de Mensagem por Etapa</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {templates.map((template, idx) => {
                    const getTomIcon = (tom) => {
                      switch(tom) {
                        case 'amigavel': return <Smile className="w-4 h-4 text-green-500" />;
                        case 'cordial': return <Heart className="w-4 h-4 text-blue-500" />;
                        case 'respeitoso': return <Meh className="w-4 h-4 text-gray-500" />;
                        case 'direto': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
                        case 'firme': return <AlertOctagon className="w-4 h-4 text-orange-500" />;
                        case 'urgente': return <Frown className="w-4 h-4 text-red-500" />;
                        default: return <MessageCircle className="w-4 h-4" />;
                      }
                    };

                    const getDiasLabel = () => {
                      if (template.dias_relativo === null) return 'Após pagamento';
                      if (template.dias_relativo < 0) return `${Math.abs(template.dias_relativo)} dias antes`;
                      if (template.dias_relativo === 0) return 'No vencimento';
                      return `${template.dias_relativo} dias após`;
                    };

                    const getBgColor = () => {
                      if (template.dias_relativo === null) return 'bg-green-50 border-green-200';
                      if (template.dias_relativo < 0) return 'bg-blue-50 border-blue-200';
                      if (template.dias_relativo === 0) return 'bg-yellow-50 border-yellow-200';
                      if (template.dias_relativo <= 5) return 'bg-orange-50 border-orange-200';
                      return 'bg-red-50 border-red-200';
                    };

                    return (
                      <Card key={idx} className={`${getBgColor()} cursor-pointer hover:shadow-md transition-shadow`}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant="outline" className="text-xs">
                              {getDiasLabel()}
                            </Badge>
                            {getTomIcon(template.tom)}
                          </div>
                          <p className="font-medium text-sm">{template.nome}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={
                              template.tom === 'amigavel' ? 'bg-green-100 text-green-800' :
                              template.tom === 'cordial' ? 'bg-blue-100 text-blue-800' :
                              template.tom === 'respeitoso' ? 'bg-gray-100 text-gray-800' :
                              template.tom === 'direto' ? 'bg-yellow-100 text-yellow-800' :
                              template.tom === 'firme' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {template.tom}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mensagens Pendentes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                Mensagens Pendentes de Envio
              </CardTitle>
            </CardHeader>
            <CardContent>
              {mensagensPendentes.length === 0 ? (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Não há mensagens pendentes de envio.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {mensagensPendentes.slice(0, 20).map((msg) => (
                    <Card key={msg.id} className="p-3">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-3">
                          {msg.canal === 'whatsapp' && (
                            <div className="p-2 rounded-lg bg-green-100">
                              <MessageCircle className="w-4 h-4 text-green-600" />
                            </div>
                          )}
                          {msg.canal === 'email' && (
                            <div className="p-2 rounded-lg bg-blue-100">
                              <Mail className="w-4 h-4 text-blue-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm">{msg.responsavel_nome}</p>
                            <p className="text-xs text-muted-foreground">Aluno: {msg.aluno_nome}</p>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{msg.assunto}</p>
                          </div>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">Pendente</Badge>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Régua Tab */}
        <TabsContent value="regua" className="space-y-4">
          {regua && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  {regua.nome}
                </CardTitle>
                <CardDescription>{regua.descricao}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-muted-foreground">
                      {regua.total_eventos} eventos configurados
                    </p>
                    <Button onClick={handleExecutarRegua} className="bg-purple-600 hover:bg-purple-700">
                      <Send className="w-4 h-4 mr-2" />
                      Executar Régua Agora
                    </Button>
                  </div>

                  {/* Timeline Visual da Régua */}
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    
                    <div className="space-y-4">
                      {regua.eventos?.map((evento, idx) => (
                        <div key={evento.id || idx} className="relative pl-10">
                          <div className={`absolute left-2 w-4 h-4 rounded-full border-2 ${
                            evento.dias_relativo_vencimento < 0 ? 'bg-blue-500 border-blue-500' :
                            evento.dias_relativo_vencimento === 0 ? 'bg-yellow-500 border-yellow-500' :
                            evento.dias_relativo_vencimento <= 7 ? 'bg-orange-500 border-orange-500' :
                            'bg-red-500 border-red-500'
                          }`}></div>
                          
                          <Card className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline">
                                    {evento.dias_relativo_vencimento === 0 
                                      ? 'No vencimento'
                                      : evento.dias_relativo_vencimento < 0 
                                        ? `${Math.abs(evento.dias_relativo_vencimento)} dias antes`
                                        : `${evento.dias_relativo_vencimento} dias após`
                                    }
                                  </Badge>
                                  <Badge className={
                                    evento.tom === 'amigavel' ? 'bg-blue-100 text-blue-800' :
                                    evento.tom === 'informativo' ? 'bg-gray-100 text-gray-800' :
                                    evento.tom === 'alerta' ? 'bg-yellow-100 text-yellow-800' :
                                    evento.tom === 'firme' ? 'bg-orange-100 text-orange-800' :
                                    'bg-red-100 text-red-800'
                                  }>
                                    {evento.tom}
                                  </Badge>
                                </div>
                                <p className="font-medium">{evento.assunto}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Canal: {evento.canal === 'whatsapp' ? 'WhatsApp' : evento.canal === 'email' ? 'E-mail' : evento.canal}
                                </p>
                              </div>
                              {evento.canal === 'whatsapp' && <MessageCircle className="w-5 h-5 text-green-600" />}
                              {evento.canal === 'email' && <Mail className="w-5 h-5 text-blue-600" />}
                            </div>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Notificações Tab */}
        <TabsContent value="notificacoes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-600" />
                Notificações Pendentes
              </CardTitle>
              <CardDescription>
                {notificacoes.length} notificações aguardando envio
              </CardDescription>
            </CardHeader>
            <CardContent>
              {notificacoes.length === 0 ? (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Não há notificações pendentes no momento.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  {notificacoes.map((notif) => (
                    <Card key={notif.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-3">
                          {notif.canal === 'whatsapp' && (
                            <div className="p-2 rounded-lg bg-green-100">
                              <MessageCircle className="w-5 h-5 text-green-600" />
                            </div>
                          )}
                          {notif.canal === 'email' && (
                            <div className="p-2 rounded-lg bg-blue-100">
                              <Mail className="w-5 h-5 text-blue-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{notif.destinatario_nome}</p>
                            <p className="text-sm text-muted-foreground">{notif.destinatario_contato}</p>
                            <p className="text-sm mt-1">{notif.assunto}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Aluno: {notif.aluno_nome}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Gerar em Lote */}
      <Dialog open={showGerarLote} onOpenChange={setShowGerarLote}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-600" />
              Gerar Cobranças em Lote
            </DialogTitle>
            <DialogDescription>
              Gere cobranças automaticamente para todos os alunos ativos com plano financeiro
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Categoria</Label>
              <Select 
                value={loteConfig.categoria} 
                onValueChange={(v) => setLoteConfig({...loteConfig, categoria: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Competência (Mês/Ano)</Label>
              <Input
                type="month"
                value={loteConfig.competencia}
                onChange={(e) => setLoteConfig({...loteConfig, competencia: e.target.value})}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="usar_plano"
                checked={loteConfig.usar_plano_financeiro}
                onChange={(e) => setLoteConfig({...loteConfig, usar_plano_financeiro: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="usar_plano">Usar valor do plano financeiro de cada aluno</Label>
            </div>

            {!loteConfig.usar_plano_financeiro && (
              <div>
                <Label>Valor Fixo</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={loteConfig.valor_fixo || ''}
                  onChange={(e) => setLoteConfig({...loteConfig, valor_fixo: parseFloat(e.target.value) || null})}
                />
              </div>
            )}

            <div>
              <Label>Descrição Personalizada (opcional)</Label>
              <Input
                placeholder="Ex: Mensalidade Escolar"
                value={loteConfig.descricao_custom}
                onChange={(e) => setLoteConfig({...loteConfig, descricao_custom: e.target.value})}
              />
            </div>
          </div>

          {resultadoLote && (
            <Alert className={resultadoLote.total_erros > 0 ? 'border-orange-500' : 'border-green-500'}>
              <AlertDescription>
                <div className="space-y-1">
                  <p><strong>Processados:</strong> {resultadoLote.total_processados}</p>
                  <p className="text-green-600"><strong>Sucesso:</strong> {resultadoLote.total_sucesso}</p>
                  {resultadoLote.total_erros > 0 && (
                    <p className="text-red-600"><strong>Erros:</strong> {resultadoLote.total_erros}</p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGerarLote(false);
              setResultadoLote(null);
            }}>
              Fechar
            </Button>
            <Button 
              onClick={handleGerarLote} 
              disabled={gerando}
              className="bg-green-600 hover:bg-green-700"
            >
              {gerando ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Gerar Títulos
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Timeline */}
      <Dialog open={!!selectedTitulo} onOpenChange={() => setSelectedTitulo(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Timeline da Cobrança</DialogTitle>
            <DialogDescription>
              {selectedTitulo?.descricao} - {selectedTitulo?.aluno_nome}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-96">
            <div className="space-y-4 p-4">
              {timeline.length === 0 ? (
                <p className="text-center text-muted-foreground">Nenhum evento registrado</p>
              ) : (
                timeline.map((evento) => (
                  <div key={evento.id} className="flex gap-3 border-l-2 border-gray-200 pl-4">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      evento.status === 'executado' ? 'bg-green-500' :
                      evento.status === 'falhou' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }`}></div>
                    <div>
                      <p className="font-medium">{evento.descricao}</p>
                      {evento.canal && (
                        <Badge variant="outline" className="mt-1">{evento.canal}</Badge>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(evento.created_at).toLocaleString('pt-BR')}
                        {evento.automatico ? ' (Automático)' : ' (Manual)'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTitulo(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Preview Mensagem */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              Preview da Mensagem
            </DialogTitle>
            <DialogDescription>
              {previewMensagem?.template_nome}
            </DialogDescription>
          </DialogHeader>
          
          {previewMensagem && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{previewMensagem.tom}</Badge>
                <Badge className="bg-green-100 text-green-800">WhatsApp</Badge>
              </div>
              
              <Card className="bg-gray-50 p-4">
                <pre className="whitespace-pre-wrap text-sm font-sans">
                  {previewMensagem.corpo}
                </pre>
              </Card>
              
              <div className="text-xs text-muted-foreground">
                <p><strong>Variáveis utilizadas:</strong></p>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {Object.entries(previewMensagem.variaveis || {}).slice(0, 8).map(([key, value]) => (
                    <span key={key}>{key}: {value}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
            <Button className="bg-green-600 hover:bg-green-700">
              <Send className="w-4 h-4 mr-2" />
              Enviar Agora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
